import React from 'react';
import dayjs from 'dayjs';
import { DatePicker, Select, Spin } from 'antd';
import { TrendingUp, ChartLine } from 'lucide-react';
import { SectionCard, EmptyState } from '../../../common';
import { brand } from '../../../../theme/tokens';

const { RangePicker } = DatePicker;
const GRID_COLOR = '#eef0f3';

const WEEKDAY_OPTIONS = [
    { value: 'all', label: 'Todos los días' },
    { value: '1', label: 'Lunes' },
    { value: '2', label: 'Martes' },
    { value: '3', label: 'Miércoles' },
    { value: '4', label: 'Jueves' },
    { value: '5', label: 'Viernes' },
    { value: '6', label: 'Sábado' },
    { value: '7', label: 'Domingo' },
];

const WEEKDAY_LABELS = {
    1: 'Lunes',
    2: 'Martes',
    3: 'Miércoles',
    4: 'Jueves',
    5: 'Viernes',
    6: 'Sábado',
    7: 'Domingo',
};

function buildLinePath(points, width, height, padding = 12) {
    if (!points || points.length === 0) return '';

    const maxValue = 100;
    const minValue = 0;
    const stepX = points.length > 1 ? width / (points.length - 1) : width;
    const usableHeight = Math.max(height - padding * 2, 1);

    return points
        .map((point, idx) => {
            const x = idx * stepX;
            const normalized = (point.value - minValue) / (maxValue - minValue || 1);
            const y = height - padding - normalized * usableHeight;
            return `${idx === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
        })
        .join(' ');
}

export default function PromoterTrend({ range, weekday, series, loading, setRange, setWeekday }) {
    const containerRef = React.useRef(null);
    const [chartWidth, setChartWidth] = React.useState(520);
    const [hoveredPoint, setHoveredPoint] = React.useState(null);

    React.useEffect(() => {
        if (!containerRef.current) return;

        const element = containerRef.current;
        const updateWidth = () => {
            const nextWidth = Math.max(320, Math.floor(element.getBoundingClientRect().width));
            setChartWidth(nextWidth);
        };

        updateWidth();

        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(updateWidth);
            observer.observe(element);
            return () => observer.disconnect();
        }

        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    const average = React.useMemo(() => {
        if (!series || series.length === 0) return 0;
        const total = series.reduce((sum, point) => sum + point.value, 0);
        return Math.round(total / series.length);
    }, [series]);

    const chartHeight = 140;
    const paddingY = 12;
    const linePath = buildLinePath(series, chartWidth, chartHeight, paddingY);

    const points = React.useMemo(() => {
        if (!series || series.length === 0) return [];
        const usableHeight = Math.max(chartHeight - paddingY * 2, 1);
        return series.map((point, idx) => {
            const x = series.length > 1 ? (chartWidth / (series.length - 1)) * idx : chartWidth / 2;
            const y = chartHeight - paddingY - (point.value / 100) * usableHeight;
            return { ...point, x, y };
        });
    }, [series, chartWidth, chartHeight, paddingY]);

    const handleMouseMove = (event) => {
        if (!points.length) return;
        const rect = event.currentTarget.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        let closest = points[0];
        let minDistance = Math.abs(points[0].x - mouseX);
        points.forEach((point) => {
            const distance = Math.abs(point.x - mouseX);
            if (distance < minDistance) {
                minDistance = distance;
                closest = point;
            }
        });
        setHoveredPoint(closest);
    };

    return (
        <SectionCard
            title="Tendencia de Cumplimiento"
            description="Porcentaje de ruta completada por día"
            icon={<TrendingUp />}
            extra={
                <div className="flex flex-wrap items-center gap-2">
                    <Select value={weekday} onChange={(value) => setWeekday(value)} className="w-40" options={WEEKDAY_OPTIONS} />
                    <RangePicker
                        allowClear={false}
                        value={range}
                        onChange={(value) => {
                            if (value && value[0] && value[1]) {
                                setRange(value);
                            }
                        }}
                    />
                </div>
            }>
            <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between text-xs text-surface-500">
                    <span className="tabular-nums">
                        {range?.[0] ? dayjs(range[0]).format('DD MMM YYYY') : '--'} -{' '}
                        {range?.[1] ? dayjs(range[1]).format('DD MMM YYYY') : '--'}
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-md bg-primary-50 px-2 py-0.5 font-medium text-primary-600 tabular-nums">
                        Promedio: {average}%
                    </span>
                </div>

                <div className="relative" ref={containerRef}>
                    {loading ? (
                        <div className="flex items-center justify-center h-40">
                            <Spin />
                        </div>
                    ) : series.length === 0 ? (
                        <EmptyState icon={<ChartLine />} title="Sin datos para este período" className="!py-6" />
                    ) : (
                        <div className="relative">
                            <svg
                                width={chartWidth}
                                height={chartHeight}
                                viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                                onMouseMove={handleMouseMove}
                                onMouseLeave={() => setHoveredPoint(null)}>
                                {[0, 50, 100].map((tick) => {
                                    const y = chartHeight - paddingY - (tick / 100) * (chartHeight - paddingY * 2);
                                    return <line key={tick} x1={0} x2={chartWidth} y1={y} y2={y} stroke={GRID_COLOR} strokeWidth="1" />;
                                })}
                                <path d={linePath} fill="none" stroke={brand[600]} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                                {points.map((point) => (
                                    <circle
                                        key={point.date}
                                        cx={point.x}
                                        cy={point.y}
                                        r={hoveredPoint?.date === point.date ? 4.5 : 3}
                                        fill={brand[600]}
                                        stroke="#fff"
                                        strokeWidth="1.5"
                                    />
                                ))}
                            </svg>
                            {hoveredPoint && (
                                <div
                                    className="pointer-events-none absolute z-10 rounded-lg border border-surface-200 bg-white px-2.5 py-1.5 text-xs text-surface-600 shadow-md"
                                    style={{
                                        left: Math.min(Math.max(hoveredPoint.x, 0), chartWidth - 140),
                                        top: Math.max(hoveredPoint.y - 40, 0)
                                    }}>
                                    <div className="font-medium text-surface-900">
                                        {WEEKDAY_LABELS[hoveredPoint.weekday] || dayjs(hoveredPoint.date).format('dddd')}
                                    </div>
                                    <div className="tabular-nums">{dayjs(hoveredPoint.date).format('DD MMM YYYY')}</div>
                                    <div className="tabular-nums">Cumplimiento: {hoveredPoint.value}%</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </SectionCard>
    );
}
